import { Guide } from '../../types/guide';

const guide: Guide = {
  slug: 'how-to-extract-text-from-a-scanned-pdf',
  title: 'How to Extract Text from a Scanned PDF',
  description:
    'Scanned PDFs have no embedded text layer, so standard extraction fails. Here\'s why that happens and what your options are today.',
  category: 'Converter',
  publishedDate: '2026-08-06',
  affiliateCategory: 'scanning',
  body: `
If you've tried converting a scanned PDF — a photocopied form, a paper document run through a scanner — and gotten an error like "No text could be extracted," this is why, and what you can do about it.

## Why scanned PDFs are different

A regular PDF (exported from Word, a website, or any digital source) embeds an actual text layer: the letters "H-e-l-l-o" are stored as text, with a font and position on the page. A scanned PDF is different — each page is really just a photograph of paper, embedded as an image. There's no text to extract because, as far as the file format is concerned, there is no text — only pixels that happen to look like letters to a human reader.

Standard PDF-to-text tools, including [pdf-to-text.com](https://pdf-to-text.com), read the embedded text layer. If there isn't one, there's nothing to read, and you'll see an error rather than a silently empty result.

## How to tell if your PDF is scanned

Try selecting text in the PDF with your cursor in a normal PDF viewer. If you can click-and-drag to highlight individual words, it has a text layer. If your cursor selects the whole page as one image (or nothing happens at all), it's scanned.

## What actually reads text out of a scanned image: OCR

Optical Character Recognition (OCR) is the technology that looks at the pixels in a scanned page and recognizes which shapes correspond to which letters — effectively "reading" the image the way a human would. It's a fundamentally different process from PDF text extraction, and it's slower and less than 100% accurate, especially on low-quality scans, unusual fonts, or handwriting.

## Current status on pdf-to-text.com

Client-side OCR (running fully in your browser, so scanned pages never leave your device — consistent with how the rest of the site works) is on the roadmap but not yet available. Today, the site will tell you clearly when a PDF has no extractable text rather than failing silently, so you know a scanned PDF needs a different approach.

## What to do in the meantime

- **If you have the original digital document** (the one that was printed before scanning), convert that instead — it'll have a full text layer.
- **If you only have the scan**, a dedicated OCR tool is currently the fastest path to get usable text out of it. Check back here — OCR support is planned for a future update.
`,
};

export default guide;
